import { AES, Base64, Hex, HmacSHA256, SHA256, Utf8, WordArray } from "crypto-es";
import { useAuthStore } from "./useAuthStore";

/**
 * Refresh tokens rotate, and the auth server treats a replaced refresh token presented later as replay: it revokes
 * the whole session family. A duplicated tab starts with the same refresh token in its sessionStorage, so both tabs
 * would eventually refresh with it. This module makes all tabs of one origin that hold a token of the same session
 * share one rotation:
 *
 * - A refresh runs inside a Web Lock named after a hash of the refresh token, so tabs holding that token take turns.
 * - The tab that rotated broadcasts the new pair keyed by the old token hash; tabs still holding the old token
 *   replace theirs at once.
 * - It also leaves a "rotated" marker in localStorage, keyed by the old token hash, for tabs that missed the
 *   broadcast because the browser froze or discarded them. Markers live as long as a refresh token and chain: a tab
 *   that slept through several rotations follows old token, next token, ... to the newest pair.
 * - Inside the lock, and whenever a tab wakes or starts, a tab first follows that chain from the token it holds and
 *   adopts the newest pair instead of presenting the replaced token to the token endpoint.
 *
 * Trade-off: markers keep token material in localStorage for up to the refresh token lifetime; each one is sealed
 * (AES + HMAC) with keys derived from the token it replaces, so only a tab still holding that token can read it.
 * Only the newest marker of a chain carries the access token; older ones keep just the next refresh token.
 *
 * Without Web Locks or BroadcastChannel the grant runs as before, uncoordinated; without localStorage only the
 * broadcast is shared. Tokens never appear in lock names, storage keys or logs; only their SHA-256 hash does.
 */

export interface SharedTokenPair {
  access_token: string
  refresh_token: string
}

export interface CrossTabLocks {
  request: <T>(name: string, callback: () => Promise<T>) => Promise<T>
}

export interface CrossTabChannel {
  postMessage: (message: unknown) => void
  addEventListener: (type: "message", listener: (event: MessageEvent) => void) => void
}

export interface CrossTabRefreshEnvironment {
  locks: CrossTabLocks | null
  channel: CrossTabChannel | null
  storage: Storage | null
  now: () => number
  /** Registers a callback for the moments a sleeping or restored tab becomes active again. */
  onWake: (callback: () => void) => void
}

export interface CrossTabRefreshOptions<T extends SharedTokenPair> {
  /** The refresh token this tab currently holds. */
  currentRefreshToken: () => string | null
  /** Replaces this tab's tokens with a pair another tab obtained for the token this tab holds. */
  applySharedTokens: (pair: T) => void
}

export interface CrossTabRefresh<T extends SharedTokenPair> {
  /** Runs `grant` for `refreshToken` at most once across tabs, or adopts the newest pair another tab obtained. */
  refresh: (refreshToken: string, grant: () => Promise<T>) => Promise<T>
  /** Adopts the newest pair other tabs obtained after the token this tab holds. Returns whether it did. */
  adoptPendingHandoff: () => boolean
  /** Drops every marker of the session this tab holds; call while the token is still set, when the person logs out. */
  forgetSession: () => void
}

export const CROSS_TAB_REFRESH_LOCK_PREFIX = "verbleif-refresh-token:";
export const CROSS_TAB_REFRESH_CHANNEL = "verbleif-refresh-token";
export const CROSS_TAB_REFRESH_MARKER_PREFIX = "refresh-token-rotated.";
/** The auth server keeps the league bundle default refresh_token_ttl of P1M; a month has at most 31 days. */
export const CROSS_TAB_REFRESH_MARKER_TTL_MS = 31 * 24 * 60 * 60 * 1000;
/** Bounds localStorage use; about four days of hourly rotations of one session, the oldest markers go first. */
export const CROSS_TAB_REFRESH_MAX_MARKERS = 100;
/** How long a pair received by broadcast stays in memory for refreshes that were already waiting for the lock. */
export const CROSS_TAB_REFRESH_HANDOFF_TTL_MS = 30_000;
/** Plain handoff records of the previous version; only removed once expired. */
export const CROSS_TAB_REFRESH_LEGACY_HANDOFF_PREFIX = "refresh-token-handoff.";

const ROTATED_MESSAGE = "refresh-token-rotated";
const KEY_DOMAIN = "verbleif-refresh-token-rotated";

interface RotatedMessage<T> {
  type: typeof ROTATED_MESSAGE
  oldHash: string
  pair: T
}

interface ReceivedPair<T> {
  expiresAt: number
  pair: T
}

interface SealedBox {
  iv: string
  ct: string
  mac: string
}

interface RotatedMarker {
  expiresAt: number
  /** Hash of the refresh token that replaced this one: the key of the next marker in the chain. */
  next: string
  /** The replacing refresh token, sealed with keys derived from the replaced one. */
  refresh: SealedBox
  /** The whole replacing pair; dropped once that pair is rotated too, so only the newest marker carries it. */
  pair?: SealedBox
}

/** One step along the chain: the replacing refresh token and, on the newest marker, its whole pair. */
interface ChainStep<T> {
  refreshToken: string
  pair: T | null
}

export function hashRefreshToken(refreshToken: string): string {
  return SHA256(refreshToken).toString();
}

function isTokenPair(value: unknown): value is SharedTokenPair {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.access_token === "string"
    && candidate.access_token !== ""
    && typeof candidate.refresh_token === "string"
    && candidate.refresh_token !== "";
}

function isRotatedMessage<T extends SharedTokenPair>(value: unknown): value is RotatedMessage<T> {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return candidate.type === ROTATED_MESSAGE && typeof candidate.oldHash === "string" && isTokenPair(candidate.pair);
}

function isSealedBox(value: unknown): value is SealedBox {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.iv === "string" && typeof candidate.ct === "string" && typeof candidate.mac === "string";
}

function parseMarker(raw: string | null): RotatedMarker | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const candidate = parsed as Record<string, unknown>;
    if (typeof candidate.expiresAt !== "number" || typeof candidate.next !== "string" || !isSealedBox(candidate.refresh)) {
      return null;
    }
    if (candidate.pair !== undefined && !isSealedBox(candidate.pair)) {
      return null;
    }
    return {
      expiresAt: candidate.expiresAt,
      next: candidate.next,
      refresh: candidate.refresh,
      ...(candidate.pair ? { pair: candidate.pair } : {}),
    };
  } catch {
    return null;
  }
}

/** Keys only a holder of `replacedToken` can derive; the storage key (its plain SHA-256) does not reveal them. */
function sealingKeys(replacedToken: string): { encryption: WordArray, mac: WordArray } {
  return {
    encryption: SHA256(`${KEY_DOMAIN}:encryption:${replacedToken}`),
    mac: SHA256(`${KEY_DOMAIN}:mac:${replacedToken}`),
  };
}

function boxMac(label: string, iv: string, ct: string, key: WordArray): string {
  return HmacSHA256(`${label}.${iv}.${ct}`, key).toString();
}

function seal(label: string, plaintext: string, replacedToken: string): SealedBox {
  const keys = sealingKeys(replacedToken);
  const iv = WordArray.random(16);
  const ciphertext = AES.encrypt(plaintext, keys.encryption, { iv }).ciphertext;
  if (!ciphertext) {
    throw new Error("AES returned no ciphertext");
  }
  const ct = ciphertext.toString(Base64);
  const ivHex = iv.toString(Hex);
  return { iv: ivHex, ct, mac: boxMac(label, ivHex, ct, keys.mac) };
}

function open(label: string, box: SealedBox, replacedToken: string): string | null {
  const keys = sealingKeys(replacedToken);
  if (boxMac(label, box.iv, box.ct, keys.mac) !== box.mac) {
    return null;
  }
  try {
    const plaintext = AES.decrypt({ ciphertext: Base64.parse(box.ct) }, keys.encryption, { iv: Hex.parse(box.iv) });
    return plaintext.toString(Utf8);
  } catch {
    return null;
  }
}

export function createCrossTabRefresh<T extends SharedTokenPair>(
  options: CrossTabRefreshOptions<T>,
  env: CrossTabRefreshEnvironment,
): CrossTabRefresh<T> {
  // Pairs this tab received by broadcast, so a refresh that was already waiting for the lock finds the pair even
  // when localStorage is unavailable.
  const received = new Map<string, ReceivedPair<T>>();

  function markerKey(oldHash: string): string {
    return `${CROSS_TAB_REFRESH_MARKER_PREFIX}${oldHash}`;
  }

  function storedMarkers(): Map<string, RotatedMarker> {
    const markers = new Map<string, RotatedMarker>();
    const storage = env.storage;
    if (!storage) {
      return markers;
    }
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(CROSS_TAB_REFRESH_MARKER_PREFIX)) {
        continue;
      }
      const marker = parseMarker(storage.getItem(key));
      if (marker) {
        markers.set(key.slice(CROSS_TAB_REFRESH_MARKER_PREFIX.length), marker);
      }
    }
    return markers;
  }

  function prefixedKeys(storage: Storage, prefix: string): string[] {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /** Removes expired or unreadable markers, then the oldest ones above the cap. */
  function sweep(): void {
    const now = env.now();
    for (const [oldHash, entry] of received) {
      if (entry.expiresAt <= now) {
        received.delete(oldHash);
      }
    }

    const storage = env.storage;
    if (!storage) {
      return;
    }

    for (const key of prefixedKeys(storage, CROSS_TAB_REFRESH_LEGACY_HANDOFF_PREFIX)) {
      const expiresAt = legacyExpiry(storage.getItem(key));
      if (expiresAt === null || expiresAt <= now) {
        storage.removeItem(key);
      }
    }

    const live: Array<{ key: string, expiresAt: number }> = [];
    for (const key of prefixedKeys(storage, CROSS_TAB_REFRESH_MARKER_PREFIX)) {
      const marker = parseMarker(storage.getItem(key));
      if (!marker || marker.expiresAt <= now) {
        storage.removeItem(key);
        continue;
      }
      live.push({ key, expiresAt: marker.expiresAt });
    }

    if (live.length <= CROSS_TAB_REFRESH_MAX_MARKERS) {
      return;
    }
    // A later marker of a chain was written later, so evicting the oldest first never breaks a chain in the middle.
    live.sort((a, b) => a.expiresAt - b.expiresAt);
    for (const { key } of live.slice(0, live.length - CROSS_TAB_REFRESH_MAX_MARKERS)) {
      storage.removeItem(key);
    }
  }

  function legacyExpiry(raw: string | null): number | null {
    if (raw === null) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        return null;
      }
      const expiresAt = (parsed as Record<string, unknown>).expiresAt;
      return typeof expiresAt === "number" ? expiresAt : null;
    } catch {
      return null;
    }
  }

  function parsePair(raw: string | null): T | null {
    if (raw === null) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      return isTokenPair(parsed) ? parsed as T : null;
    } catch {
      return null;
    }
  }

  /** What replaced `token`, from its marker or else from a broadcast this tab received. */
  function stepFrom(token: string): ChainStep<T> | null {
    const now = env.now();
    const oldHash = hashRefreshToken(token);

    const marker = env.storage ? parseMarker(env.storage.getItem(markerKey(oldHash))) : null;
    if (marker && marker.expiresAt > now) {
      const refreshToken = open("refresh", marker.refresh, token);
      if (refreshToken) {
        const pair = marker.pair ? parsePair(open("pair", marker.pair, token)) : null;
        return { refreshToken, pair: pair && pair.refresh_token === refreshToken ? pair : null };
      }
    }

    const broadcast = received.get(oldHash);
    if (broadcast && broadcast.expiresAt > now) {
      return { refreshToken: broadcast.pair.refresh_token, pair: broadcast.pair };
    }
    return null;
  }

  /** Follows the chain from `token` to the newest pair, or null when nothing replaced `token`. */
  function newestPairAfter(token: string): T | null {
    const visited = new Set<string>([token]);
    let last: ChainStep<T> | null = null;
    let current = token;
    for (let hop = 0; hop < CROSS_TAB_REFRESH_MAX_MARKERS; hop++) {
      const step = stepFrom(current);
      if (!step || visited.has(step.refreshToken)) {
        break;
      }
      visited.add(step.refreshToken);
      last = step;
      current = step.refreshToken;
    }
    if (!last) {
      return null;
    }
    if (!last.pair) {
      // Only reachable when a later marker went missing; presenting any token of the chain would be replay.
      console.warn("[CrossTabRefresh] The rotation chain of this tab's refresh token is incomplete");
      return null;
    }
    return last.pair;
  }

  function publish(oldToken: string, oldHash: string, pair: T): void {
    const storage = env.storage;
    if (storage) {
      try {
        // The pair this rotation replaced no longer needs to be readable from older markers; only its token does.
        for (const [hash, marker] of storedMarkers()) {
          if (marker.next !== oldHash || !marker.pair) {
            continue;
          }
          const withoutPair: RotatedMarker = { expiresAt: marker.expiresAt, next: marker.next, refresh: marker.refresh };
          storage.setItem(markerKey(hash), JSON.stringify(withoutPair));
        }
        const marker: RotatedMarker = {
          expiresAt: env.now() + CROSS_TAB_REFRESH_MARKER_TTL_MS,
          next: hashRefreshToken(pair.refresh_token),
          refresh: seal("refresh", pair.refresh_token, oldToken),
          pair: seal("pair", JSON.stringify(pair), oldToken),
        };
        storage.setItem(markerKey(oldHash), JSON.stringify(marker));
        sweep();
      } catch (e) {
        // A full or blocked storage only costs the marker for sleeping tabs; the broadcast still reaches live tabs.
        console.warn("[CrossTabRefresh] Could not store the rotated marker", e instanceof Error ? e.name : "");
      }
    }
    const message: RotatedMessage<T> = { type: ROTATED_MESSAGE, oldHash, pair };
    env.channel?.postMessage(message);
  }

  function adoptPendingHandoff(): boolean {
    sweep();
    const current = options.currentRefreshToken();
    if (!current) {
      return false;
    }
    const pair = newestPairAfter(current);
    if (!pair || pair.refresh_token === current) {
      return false;
    }
    console.log("[CrossTabRefresh] Adopted the newest token pair another tab obtained");
    options.applySharedTokens(pair);
    return true;
  }

  function forgetSession(): void {
    received.clear();
    const storage = env.storage;
    const current = options.currentRefreshToken();
    if (!storage || !current) {
      return;
    }
    // Markers link old hash to next hash; the session is every marker connected to the token this tab holds.
    const markers = storedMarkers();
    const session = new Set<string>([hashRefreshToken(current)]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const [hash, marker] of markers) {
        if (session.has(hash) === session.has(marker.next)) {
          continue;
        }
        session.add(hash);
        session.add(marker.next);
        grew = true;
      }
    }
    for (const hash of session) {
      storage.removeItem(markerKey(hash));
    }
  }

  function onMessage(event: MessageEvent): void {
    if (!isRotatedMessage<T>(event.data)) {
      return;
    }
    const { oldHash, pair } = event.data;
    received.set(oldHash, { expiresAt: env.now() + CROSS_TAB_REFRESH_HANDOFF_TTL_MS, pair });

    const current = options.currentRefreshToken();
    if (!current || current === pair.refresh_token || hashRefreshToken(current) !== oldHash) {
      return;
    }
    console.log("[CrossTabRefresh] Another tab rotated this tab's refresh token, taking over the new pair");
    options.applySharedTokens(pair);
  }

  async function refresh(refreshToken: string, grant: () => Promise<T>): Promise<T> {
    const { locks, channel } = env;
    if (!locks || !channel) {
      return grant();
    }

    const oldHash = hashRefreshToken(refreshToken);

    return locks.request(`${CROSS_TAB_REFRESH_LOCK_PREFIX}${oldHash}`, async () => {
      const adopted = newestPairAfter(refreshToken);
      if (adopted) {
        console.log("[CrossTabRefresh] Another tab already refreshed this token, adopting the newest pair");
        return adopted;
      }

      const pair = await grant();
      publish(refreshToken, oldHash, pair);
      return pair;
    });
  }

  if (env.channel) {
    env.channel.addEventListener("message", onMessage);
    env.onWake(() => {
      adoptPendingHandoff();
    });
    adoptPendingHandoff();
  }

  return { refresh, adoptPendingHandoff, forgetSession };
}

export function browserCrossTabRefreshEnvironment(): CrossTabRefreshEnvironment {
  const lockManager = typeof navigator !== "undefined" ? navigator.locks : undefined;
  const locks: CrossTabLocks | null = lockManager && typeof lockManager.request === "function"
    ? { request: (name, callback) => lockManager.request(name, () => callback()) }
    : null;

  const channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CROSS_TAB_REFRESH_CHANNEL) : null;

  let storage: Storage | null = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch {
    storage = null;
  }

  return {
    locks,
    channel,
    storage,
    now: () => Date.now(),
    onWake: (callback) => {
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            callback();
          }
        });
      }
      if (typeof window !== "undefined") {
        window.addEventListener("pageshow", callback);
        window.addEventListener("focus", callback);
      }
    },
  };
}

let shared: CrossTabRefresh<SharedTokenPair> | null = null;

/** The one coordinator of this tab, bound to the auth store. Web and cms both reach it through the auth plugin. */
export function useCrossTabRefresh(): CrossTabRefresh<SharedTokenPair> {
  if (shared) {
    return shared;
  }
  shared = createCrossTabRefresh<SharedTokenPair>({
    currentRefreshToken: () => useAuthStore().getRefreshToken.value,
    applySharedTokens: (pair) => {
      const authStore = useAuthStore();
      authStore.setToken(pair.access_token);
      authStore.setRefreshToken(pair.refresh_token);
    },
  }, browserCrossTabRefreshEnvironment());
  return shared;
}

/** Test-only: forget the shared coordinator. */
export function __resetCrossTabRefreshForTests(): void {
  shared = null;
}
